import { useState } from 'react';
import { ArrowRight, BrainCircuit, Check, Clock3, Goal, Plus, Sparkles, Trash2 } from 'lucide-react';
import { bridge } from './bridge';
import { uuid, type Entity } from './types';

type SuggestedTask = {
  title: string;
  firstStep: string;
  minutes: number;
  priority: 'main' | 'support';
};

type Props = {
  goals: Entity[];
  tasks: Entity[];
  save: (type: Entity['type'], id: string, data: Record<string, unknown>, deleted?: boolean) => Promise<Entity>;
  date: string;
};

export default function GoalPlanner({ goals, tasks, save, date }: Props) {
  const [title, setTitle] = useState('开发面向越南人的汉语课程');
  const [description, setDescription] = useState('包含发音、汉字、词汇和课文，让越南学生逐步学会汉语。');
  const [prompt, setPrompt] = useState('');
  const [reply, setReply] = useState('');
  const [suggestions, setSuggestions] = useState<SuggestedTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingGoalId, setDeletingGoalId] = useState<string | null>(null);
  const [goalMessage, setGoalMessage] = useState('');

  const addGoal = async () => {
    const nextTitle = title.trim();
    if (!nextTitle) return;
    if (goals.some((goal) => String(goal.data.title || '').trim().toLocaleLowerCase() === nextTitle.toLocaleLowerCase())) {
      setGoalMessage('这个目标已经存在，无需重复添加。');
      return;
    }
    await save('goal', uuid(), { title: nextTitle, description: description.trim(), createdAt: Date.now() });
    setTitle('');
    setDescription('');
    setGoalMessage('目标已保存。');
  };

  const removeGoal = async (goal: Entity) => {
    if (deletingGoalId !== goal.id) {
      setDeletingGoalId(goal.id);
      return;
    }
    await save('goal', goal.id, goal.data, true);
    setDeletingGoalId(null);
    setGoalMessage('目标已删除。');
  };

  const ask = async () => {
    const question = prompt.trim() || `请帮我把目标“${title}”拆成今天可以开始的具体步骤。`;
    setLoading(true);
    setReply('');
    setSuggestions([]);
    try {
      await save('chat', uuid(), { role: 'user', content: question, createdAt: Date.now() });
      const result = await bridge.api('/api/ai/chat', 'POST', {
        messages: [{ role: 'user', content: question }],
        context: {
          goal: { title, description },
          tasks: tasks.map((task) => task.data),
          day: { date },
        },
      });
      const responseText = String(result.reply || '');
      setReply(responseText);
      setSuggestions(Array.isArray(result.tasks) ? result.tasks : []);
      await save('chat', uuid(), { role: 'assistant', content: responseText, createdAt: Date.now() });
    } catch (error) {
      setReply(`暂时无法调用 AI：${error instanceof Error ? error.message : '未知错误'}`);
    } finally {
      setLoading(false);
    }
  };

  const adopt = async (suggestion: SuggestedTask, index: number) => {
    await save('task', uuid(), {
      title: suggestion.title,
      firstStep: suggestion.firstStep || '打开相关材料，先做五分钟',
      minutes: Math.max(5, Number(suggestion.minutes) || 30),
      priority: suggestion.priority === 'main' ? 'main' : 'support',
      completed: false,
      date,
      createdAt: Date.now(),
    });
    setSuggestions((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  return (
    <section className="two-col goals">
      <div className="panel">
        <span className="eyebrow">长期方向</span>
        <h3>让目标变成下一步</h3>
        {goals.map((goal) => (
          <div className="goal-card" key={goal.id}>
            <Goal />
            <div>
              <b>{goal.data.title}</b>
              <p>{goal.data.description}</p>
            </div>
            <button className={deletingGoalId === goal.id ? 'goal-delete confirming' : 'goal-delete'} onClick={() => removeGoal(goal)} aria-label={`删除目标 ${goal.data.title}`}>
              <Trash2 size={15} />{deletingGoalId === goal.id ? '确认删除' : '删除'}
            </button>
          </div>
        ))}
        <div className="composer">
          <input value={title} onChange={(event) => { setTitle(event.target.value); setGoalMessage(''); }} aria-label="目标名称" placeholder="目标名称" />
          <textarea value={description} onChange={(event) => setDescription(event.target.value)} aria-label="目标说明" />
          <button onClick={addGoal} disabled={!title.trim()}><Plus />保存目标</button>
          {goalMessage && <p className="form-msg">{goalMessage}</p>}
        </div>
      </div>

      <div className="panel ai">
        <div className="panel-title">
          <div>
            <span className="eyebrow">AI 规划伙伴</span>
            <h3>把模糊目标拆小</h3>
          </div>
          <Sparkles />
        </div>
        <textarea
          value={prompt}
          onChange={(event) => setPrompt(event.target.value)}
          placeholder="例如：我今天状态一般，帮我决定课程开发从哪里开始。"
        />
        <button className="primary" onClick={ask} disabled={loading}>
          <BrainCircuit />{loading ? '正在思考…' : '请 AI 帮我拆解'}
        </button>
        {reply && <div className="ai-reply">{reply}</div>}
        {suggestions.length > 0 && (
          <div className="suggestions">
            <span className="eyebrow">建议任务 · 由你决定是否加入</span>
            {suggestions.map((suggestion, index) => (
              <article className="suggestion" key={`${suggestion.title}-${index}`}>
                <div>
                  <b>{suggestion.title}</b>
                  <p><ArrowRight size={14} />{suggestion.firstStep}</p>
                  <small><Clock3 size={13} />约 {suggestion.minutes || 30} 分钟 · {suggestion.priority === 'main' ? '主线' : '辅助'}</small>
                </div>
                <button onClick={() => adopt(suggestion, index)}><Check size={15} />加入今天</button>
              </article>
            ))}
          </div>
        )}
        <small>AI 只提出建议。任务只有在你点击“加入今天”后才会保存。</small>
      </div>
    </section>
  );
}
